import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      // Attach granular permissions for staff roles (admin/super_admin/dozent/auditor)
      if (["admin", "super_admin", "dozent", "auditor"].includes(userData.role)) {
        try {
          const permRes = await axios.get(`${API}/admin/my-permissions`);
          userData.allowed_areas = permRes.data?.allowed_areas || [];
        } catch (e) {
          userData.allowed_areas = [];
        }
      }
      setUser(userData);
    } catch (error) {
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    // Attach granular permissions
    if (["admin", "super_admin", "dozent", "auditor"].includes(userData.role)) {
      try {
        const permRes = await axios.get(`${API}/admin/my-permissions`);
        userData.allowed_areas = permRes.data?.allowed_areas || [];
      } catch (e) {
        userData.allowed_areas = [];
      }
    }
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
